import { Box, Text, useThemeUI } from "theme-ui";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import castArray from "lodash/fp/castArray";
import concat from "lodash/fp/concat";
import dropRight from "lodash/fp/dropRight";
import flow from "lodash/fp/flow";
import uniq from "lodash/fp/uniq";
import filter from "lodash/fp/filter";
import head from "lodash/fp/head";
import nl2br from "react-nl2br";

import { selectUserChoiceValues } from "store/app";
import { Progress, HorizontalScrollView } from "components";
import { shouldBeDisplayed } from "utils";

import Fields from "./Fields";
import * as styles from "./Customisations.styles";

function Customisations({
  customisations = [],
  selectedCustomisationId,
  UIVersion,
}) {
  const {
    theme: { isMobile, isDesktop },
  } = useThemeUI();

  const userChoiceValues = useSelector(selectUserChoiceValues);

  const [focusedFields, _setFocusedFields] = useState([]);

  const setFocusedFields = flow(
    castArray,
    concat(focusedFields),
    uniq,
    _setFocusedFields
  );

  const goBackFocusedFields = () => _setFocusedFields(dropRight(1));

  const [scrollPositions, setScrollPositions] = useState({});

  useEffect(() => {
    _setFocusedFields([]);
  }, [selectedCustomisationId]);

  const [scrollPositionId] = [
    selectedCustomisationId,
    ...focusedFields,
  ].reverse();

  const selectedCustomisation =
    customisations.find(({ id }) => selectedCustomisationId === id) || {};

  const conditionalSubtitle = flow(
    castArray,
    filter(shouldBeDisplayed(userChoiceValues)),
    head
  )(selectedCustomisation?.conditionalSubtitle);

  const conditionalTitle = flow(
    castArray,
    filter(shouldBeDisplayed(userChoiceValues)),
    head
  )(selectedCustomisation?.conditionalTitle);

  return (
    <Box sx={styles.customisations({ UIVersion })} data-cy="customisations">
      <Box sx={UIVersion === 2 ? styles.formFieldsV2 : styles.formFields}>
        {isMobile && UIVersion !== 2 ? (
          <Fields
            selectedCustomisation={selectedCustomisation}
            userChoiceValues={userChoiceValues}
          />
        ) : (
          <HorizontalScrollView
            key={selectedCustomisationId}
            sideGutter={20}
            itemGap={20}
            height={isMobile ? 40 : 100}
            sideButtonWidth={isMobile ? 40 : 80}
            scrollPositions={scrollPositions}
            scrollPositionId={scrollPositionId}
            onScroll={(scrollPosition) => {
              setScrollPositions((prev) => ({
                ...prev,
                [scrollPositionId]: scrollPosition,
              }));
            }}
          >
            {conditionalTitle && (
              <Text
                variant="copy2"
                sx={{
                  color: "black",
                  fontSize: "16px",
                  fontFamily: "roboto",
                  textAlign: "center",
                }}
              >
                {nl2br(conditionalTitle.content)}
              </Text>
            )}
            <Fields
              selectedCustomisation={selectedCustomisation}
              userChoiceValues={userChoiceValues}
              focusedField={focusedFields}
              setFocusedField={setFocusedFields}
              goBackFocusedFields={goBackFocusedFields}
            />
          </HorizontalScrollView>
        )}
      </Box>
      {conditionalSubtitle && (
        <Text
          variant="copy2"
          sx={{
            display: "block",
            padding: "0 40px",
            transform: isMobile ? "translateY(10px)" : "translateY(-6px)",
            color: "black",
            fontSize: "12px",
            lineHeight: "16px",
            fontFamily: "roboto",
            textAlign: "center",
          }}
        >
          {nl2br(conditionalSubtitle.content)}
        </Text>
      )}

      {!isDesktop && UIVersion !== 2 && customisations.length > 1 && (
        <Box sx={styles.progressContainer}>
          <Progress
            tabs={customisations}
            selectedTabId={selectedCustomisationId}
            userChoiceValues={userChoiceValues}
          />
        </Box>
      )}
    </Box>
  );
}

export default Customisations;
